import { FC, useState } from "react";
import {
  ErrorBoundaryPropsWithComponent,
  FallbackProps,
  ErrorBoundary as ReactErrorBoundary,
} from "react-error-boundary";

import { appsignal } from "@/appsignal";
import { useTranslation } from "@/i18n/client";

type ErrorBoundaryProps = Omit<ErrorBoundaryPropsWithComponent, "FallbackComponent"> &
  Partial<Pick<ErrorBoundaryPropsWithComponent, "FallbackComponent">>;

export const ErrorBoundary: FC<ErrorBoundaryProps> = ({
  FallbackComponent = ErrorFallback,
  ...props
}) => {
  return (
    <ReactErrorBoundary FallbackComponent={FallbackComponent} onError={reportError} {...props} />
  );
};

export const ErrorFallback: FC<FallbackProps> = ({ error }) => {
  const { t } = useTranslation("common");

  return (
    <div role="alert" className="rounded-md bg-error p-4 text-error-contrast">
      <div className="text-lg font-bold ">{t("errorBoundary.title")}</div>
      <div>{t("errorBoundary.description")}</div>
      {error instanceof Error && <ErrorDetails error={error} />}
    </div>
  );
};

export const ErrorDetails: FC<{ error: Error }> = ({ error }) => {
  const [errorLines, setErrorLines] = useState<number | undefined>(5);

  return (
    <div className="mt-2 overflow-y-scroll border-l-4 border-background pl-4">
      {error.message}

      {error.stack && (
        <div className="mt-2 whitespace-nowrap font-mono text-sm ">
          {error.stack
            .split("\n")
            .slice(0, errorLines)
            .map((line, index) => (
              <div key={index} className="">
                {line}
              </div>
            ))}
          {errorLines && (
            <button
              className="border-2"
              onClick={() => setErrorLines(undefined)}
            >
              Toon alle
            </button>
          )}
        </div>
      )}
    </div>
  );
};

function reportError(error: Error) {
  console.log("ErrorBoundary error", error);
  void appsignal.sendError(error, {});
}
